/**
 * lx-debug-view —— 调试页（覆盖层）
 *
 * M5 需求「调试页：查看能力清单及每条的真实实现状态 + AI 调用日志」。
 *
 * 这个页面是这个项目**透明度**的体现：
 *   · 能力清单直接来自 registry.describeForPrompt() —— 和喂给 AI 的**完全同一份**。
 *     你在这一页看到什么，AI 就看到什么。没有第二份文档可以对不上。
 *   · AI 调用日志记录每一次「用户说了什么 → 模型返回了什么 → 清洗成了什么」。
 *     出问题时不用猜，翻日志就行。
 *
 * 这个页面本身也是一个证据：如果 AI 说「我做不到 X」，你在这里能看到
 * 能力表里确实没有 X —— 它的诚实是可验证的。
 */

import { LxElement, define } from '@/core/base-element';
import { registry, describeHandles } from '@/il/register';
import { popOverlay } from '@/core/ui-store';
import { tryParse } from '@/il/parser';
import { evaluate } from '@/il/evaluator';
import { settingsRepo } from '@/settings/settings';
import { trace } from '@/ai/trace';
import type { EvalResult } from '@/types/il';

interface LogRow {
  at: number;
  input: string;
  raw: string;
  code: string;
}

/** IL 直连执行的一次结果 */
interface ILRun {
  at: number;
  code: string;
  /** 解析阶段就失败时的错误文案 */
  parseError: string;
  results: EvalResult[];
}

export class LxDebugView extends LxElement {
  private _tab: 'capabilities' | 'logs' | 'il' = 'capabilities';
  private _logs: LogRow[] = [];
  /** IL 直连的执行历史（最近 20 次） */
  private _runs: ILRun[] = [];
  /** IL 输入框的草稿 —— 切标签页不该丢内容 */
  private _draft = '';
  private _running = false;

  protected styles(): string {
    return `
      :host {
        position: absolute;
        inset: 0;
        z-index: var(--z-overlay);
        display: flex;
        flex-direction: column;
        background: var(--c-bg);
      }

      header {
        flex-shrink: 0;
        display: flex; align-items: center; gap: var(--sp-3);
        padding: var(--sp-3) var(--layout-content-pad-x);
        padding-top: calc(var(--sp-3) + env(safe-area-inset-top, 0px));
        background: var(--c-surface);
        border-bottom: 1px solid var(--c-border);
      }
      .back {
        width: 34px; height: 34px;
        display: grid; place-items: center;
        border-radius: var(--radius-full);
        color: var(--c-fg);
      }
      .back:hover { background: var(--c-surface-2); }
      .back svg { width: 20px; height: 20px; fill: none; stroke: currentColor; stroke-width: 1.8; }
      .title { flex: 1; font-size: var(--fs-lg); font-weight: var(--fw-medium); color: var(--c-fg); }

      .tabs {
        flex-shrink: 0;
        display: flex;
        border-bottom: 1px solid var(--c-border);
        background: var(--c-surface);
      }
      .tabs button {
        flex: 1;
        padding: var(--sp-3);
        border: none;
        background: none;
        color: var(--c-fg-subtle);
        font-size: var(--fs-sm);
        cursor: pointer;
        border-bottom: 2px solid transparent;
      }
      .tabs button.active {
        color: var(--c-primary);
        border-bottom-color: var(--c-primary);
        font-weight: var(--fw-medium);
      }

      .body {
        flex: 1; min-height: 0;
        overflow-y: auto;
        overscroll-behavior: contain;
        padding: var(--sp-4) var(--layout-content-pad-x);
      }

      .hint {
        font-size: var(--fs-xs);
        color: var(--c-fg-subtle);
        line-height: var(--lh-relaxed, 1.7);
        margin-bottom: var(--sp-4);
        padding: var(--sp-3);
        background: var(--c-surface-2);
        border-radius: var(--radius-sm);
      }

      .obj { margin-bottom: var(--sp-5); }
      .obj-name {
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: var(--fs-md);
        color: var(--c-primary);
        font-weight: var(--fw-medium);
      }
      .obj-desc { font-size: var(--fs-xs); color: var(--c-fg-subtle); margin: 2px 0 var(--sp-2); }

      .method {
        display: flex; align-items: flex-start; gap: var(--sp-2);
        padding: var(--sp-2) 0;
        border-top: 1px solid var(--c-border);
        font-size: var(--fs-sm);
      }
      .sig {
        font-family: var(--font-mono, ui-monospace, monospace);
        color: var(--c-fg);
        flex: 1;
        min-width: 0;
        word-break: break-word;
      }
      .sig .m { color: var(--c-fg); font-weight: var(--fw-medium); }
      .sig .p { color: var(--c-fg-subtle); }
      .dot {
        width: 7px; height: 7px; border-radius: 50%;
        background: #22c55e;
        margin-top: 6px;
        flex-shrink: 0;
      }
      .mdesc { font-size: var(--fs-xs); color: var(--c-fg-subtle); margin-top: 2px; }

      .handles {
        margin-top: var(--sp-2);
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: var(--fs-xs);
        color: var(--c-fg-muted);
        white-space: pre-wrap;
      }

      .log {
        border: 1px solid var(--c-border);
        border-radius: var(--radius-sm);
        padding: var(--sp-3);
        margin-bottom: var(--sp-3);
        background: var(--c-surface);
      }
      .log-time { font-size: var(--fs-xs); color: var(--c-fg-subtle); margin-bottom: var(--sp-1); }
      .log-line { font-size: var(--fs-sm); color: var(--c-fg); margin-top: var(--sp-2); }
      .log-line b { color: var(--c-fg-subtle); font-weight: normal; }
      pre {
        margin: var(--sp-1) 0 0;
        padding: var(--sp-2);
        background: var(--c-surface-2);
        border-radius: var(--radius-xs, 4px);
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: var(--fs-xs);
        color: var(--c-fg-muted);
        white-space: pre-wrap;
        word-break: break-word;
        max-height: 160px;
        overflow: auto;
      }
      .empty {
        text-align: center; color: var(--c-fg-subtle);
        font-size: var(--fs-sm); padding: var(--sp-6) 0;
      }

      /* ---------- IL 直连执行 ---------- */
      .il-form {
        margin-bottom: var(--sp-4);
      }
      .il-form textarea {
        width: 100%;
        min-height: 92px;
        padding: var(--sp-3);
        border: 1px solid var(--c-border);
        border-radius: var(--radius-sm);
        background: var(--c-surface);
        color: var(--c-fg);
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: var(--fs-sm);
        line-height: var(--lh-base);
        resize: vertical;
      }
      .il-form textarea:focus {
        outline: none;
        border-color: var(--c-accent);
      }
      .il-actions {
        display: flex; gap: var(--sp-2); margin-top: var(--sp-2);
      }
      .il-actions button {
        padding: var(--sp-2) var(--sp-4);
        border-radius: var(--radius-md);
        font-size: var(--fs-sm);
        border: 1px solid var(--c-border);
        background: var(--c-surface-2);
        color: var(--c-fg);
      }
      .il-actions button.primary {
        background: var(--c-primary);
        color: var(--c-primary-fg);
        border-color: transparent;
        font-weight: var(--fw-medium);
      }
      .il-actions button:disabled { opacity: .55; cursor: not-allowed; }

      .run {
        border: 1px solid var(--c-border);
        border-radius: var(--radius-sm);
        padding: var(--sp-3);
        margin-bottom: var(--sp-3);
        background: var(--c-surface);
      }
      .run-head {
        display: flex; align-items: center; justify-content: space-between;
        gap: var(--sp-2);
        font-size: var(--fs-xs);
        color: var(--c-fg-subtle);
        margin-bottom: var(--sp-2);
      }
      .run-code {
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: var(--fs-xs);
        color: var(--c-fg);
        background: var(--c-surface-2);
        border-radius: var(--radius-xs, 4px);
        padding: var(--sp-2);
        white-space: pre-wrap;
        word-break: break-word;
      }
      .res {
        display: flex; gap: var(--sp-2);
        font-size: var(--fs-sm);
        line-height: var(--lh-base);
        margin-top: var(--sp-2);
        padding-top: var(--sp-2);
        border-top: 1px dashed var(--c-border);
      }
      .res .mark { flex-shrink: 0; font-family: var(--font-mono, monospace); }
      .res.ok .mark { color: var(--c-success, #22c55e); }
      .res.fail .mark { color: var(--c-danger, #e5484d); }
      .res.fail { color: var(--c-danger, #e5484d); }
      .res .cmd {
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: var(--fs-xs);
        color: var(--c-fg-subtle);
        display: block;
        margin-bottom: 2px;
      }

      /* 查询方法的返回值 —— 默认折叠，点开才看 */
      details.val { margin-top: var(--sp-2); }
      details.val summary {
        font-size: var(--fs-xs);
        color: var(--c-primary);
        cursor: pointer;
      }

      .badge.query {
        font-size: 10px;
        padding: 1px 5px;
        border-radius: var(--radius-full);
        background: var(--c-surface-3);
        color: var(--c-fg-muted);
        margin-left: var(--sp-1);
      }
    `;
  }

  protected render(): string {
    return `
      <header>
        <button class="back" data-back aria-label="返回">${BACK_ICON}</button>
        <div class="title">调试</div>
      </header>
      <div class="tabs">
        <button data-tab="capabilities" class="${this._tab === 'capabilities' ? 'active' : ''}">能力清单</button>
        <button data-tab="il" class="${this._tab === 'il' ? 'active' : ''}">直接执行</button>
        <button data-tab="logs" class="${this._tab === 'logs' ? 'active' : ''}">AI 日志</button>
      </div>
      <div class="body">
        ${
          this._tab === 'capabilities'
            ? this.renderCapabilities()
            : this._tab === 'il'
              ? this.renderIL()
              : this.renderLogs()
        }
      </div>
    `;
  }

  /**
   * 「直接执行」标签 —— 绕过大模型，把 IL 直接喂给解释器。
   *
   * 为什么这个入口值得存在（不是玩具）：
   *   灵讯的链路是「自然语言 → AI → IL → 解释器 → 宿主 API」。
   *   当一句话没按预期生效时，有两种可能：
   *     ① AI 翻译错了（IL 本身是对的，模型写歪了）
   *     ② 宿主 API 有问题（IL 对，但 API 实现有 bug）
   *   直接在输入栏试只会得到「失败」，分不清是哪一种。
   *   这里手写一条正确的 IL 跑一遍，立刻就能定位到 ② 还是 ①。
   *
   * 它依然**受完整约束**：走同一个 Lexer/Parser，走同一个白名单
   * registry。手写 `evil.hack()` 一样会得到 NameError。这不是后门，
   * 只是换了一个"输入源"。
   */
  private renderIL(): string {
    const runs = this._runs
      .map((r) => this.renderRun(r))
      .join('');

    return `
      <div class="hint">
        这里输入的代码会<b>跳过 AI 直接执行</b> —— 走同一个解释器与同一份能力白名单，
        但不会被大模型翻译。<br>
        用途：确认某句指令「到底是 AI 翻译错了，还是宿主 API 本身有问题」。<br>
        多条指令用 <code>;</code> 分隔。手写不存在的名字一样会报错（这是好事）。
      </div>

      <div class="il-form">
        <textarea data-il-input spellcheck="false"
          placeholder="setting.setTheme('dark'); app.openSettings()"
          aria-label="要直接执行的指令">${this.esc(this._draft)}</textarea>
        <div class="il-actions">
          <button class="primary" type="button" data-il-run ${this._running ? 'disabled' : ''}>
            ${this._running ? '执行中…' : '执行'}
          </button>
          <button type="button" data-il-clear ${this._running ? 'disabled' : ''}>清空历史</button>
        </div>
      </div>

      ${
        runs ||
        `<div class="empty">还没有手写过指令。<br>试试上面那行示例。</div>`
      }
    `;
  }

  private renderRun(run: ILRun): string {
    const time = new Date(run.at).toLocaleTimeString('zh-CN');
    const okCount = run.results.filter((r) => r.ok).length;
    const failCount = run.results.filter((r) => !r.ok).length;

    const head = run.parseError
      ? '<span style="color:var(--c-danger)">解析失败</span>'
      : failCount > 0
        ? `<span style="color:var(--c-danger)">${okCount} 成功 · ${failCount} 失败</span>`
        : `<span style="color:var(--c-success)">${okCount} 条全部成功</span>`;

    const rows = run.parseError
      ? `<div class="res fail"><span class="mark">✗</span><span>${this.esc(run.parseError)}</span></div>`
      : run.results
          .map((r) => {
            if (!r.ok) {
              return `<div class="res fail">
                <span class="mark">✗</span>
                <span><span class="cmd">${this.esc(r.command)}</span>
                ${this.esc(r.error?.kind ?? '错误')}：${this.esc(r.error?.message ?? '未知错误')}</span>
              </div>`;
            }
            const hasValue = r.value !== undefined;
            return `<div class="res ok">
              <span class="mark">✓</span>
              <span><span class="cmd">${this.esc(r.command)}${
                r.isQuery ? '<span class="badge query">查</span>' : ''
              }</span>
              ${this.esc(r.desc)}
              ${
                hasValue
                  ? `<details class="val"><summary>看返回值</summary><pre>${this.esc(
                      safePreview(r.value),
                    )}</pre></details>`
                  : ''
              }</span>
            </div>`;
          })
          .join('');

    return `
      <div class="run">
        <div class="run-head"><span>${this.esc(time)}</span>${head}</div>
        <div class="run-code">${this.esc(run.code)}</div>
        ${rows}
      </div>
    `;
  }

  private renderCapabilities(): string {
    const objects = registry.listObjects();
    const handleText = describeHandles();

    const blocks = objects
      .map((o) => {
        const methods = o.methods
          .map(
            (m) => `
            <div class="method">
              <span class="dot" title="已真实实现"></span>
              <div class="sig">
                <div><span class="m">${this.esc(o.name)}.${this.esc(m.name)}</span><span class="p">(${this.esc(formatParamsText(m.params))})</span></div>
                <div class="mdesc">${this.esc(m.desc)}</div>
              </div>
            </div>`,
          )
          .join('');
        return `
          <div class="obj">
            <div class="obj-name">${this.esc(o.name)}</div>
            <div class="obj-desc">${this.esc(o.desc)}</div>
            ${methods}
          </div>
        `;
      })
      .join('');

    return `
      <div class="hint">
        下面这份清单和喂给 AI 的提示词**是同一份**（同一个 <code>registry</code> 生成）。<br>
        如果你看到某个能力不在列表里，AI 就真的不会它 —— 它会诚实说「做不到」。<br>
        绿点表示该方法已真实实现（不是占位）。
      </div>
      ${blocks}
      ${
        handleText
          ? `<div class="obj"><div class="obj-name">当前可引用的对象</div>
             <div class="handles">${this.esc(handleText)}</div></div>`
          : ''
      }
    `;
  }

  private renderLogs(): string {
    if (this._logs.length === 0) {
      return `<div class="empty">还没有 AI 调用记录。<br>去输入栏说一句话试试。</div>`;
    }
    return this._logs
      .map(
        (l) => `
        <div class="log">
          <div class="log-time">${new Date(l.at).toLocaleString('zh-CN')}</div>
          <div class="log-line"><b>你说：</b>${this.esc(l.input)}</div>
          <div class="log-line"><b>模型返回：</b></div>
          <pre>${this.esc(l.raw || '（空）')}</pre>
          <div class="log-line"><b>清洗后执行：</b></div>
          <pre>${this.esc(l.code || '（没有可执行的指令）')}</pre>
        </div>`,
      )
      .join('');
  }

  protected onMounted(): void {
    this.$('[data-back]')?.addEventListener('click', () => popOverlay());
    this.$$('[data-tab]').forEach((btn) => {
      btn.addEventListener('click', () => {
        // 切页面前先把草稿存下来，否则重渲染会把用户写一半的代码清掉
        this.syncDraft();
        this._tab = (btn as HTMLElement).dataset.tab as 'capabilities' | 'logs' | 'il';
        this.update();
      });
    });

    const ta = this.$('[data-il-input]') as HTMLTextAreaElement | null;
    if (ta) {
      // 输入即存草稿；不 update，避免每敲一个字就重渲染
      ta.addEventListener('input', () => {
        this._draft = ta.value;
      });
      // Ctrl/Cmd + Enter 直接执行 —— 写代码的人的习惯
      ta.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          void this.runIL();
        }
      });
    }

    this.$('[data-il-run]')?.addEventListener('click', () => void this.runIL());

    this.$('[data-il-clear]')?.addEventListener('click', () => {
      this._runs = [];
      this.update();
    });
  }

  /** 把 textarea 的当前值同步进 _draft */
  private syncDraft(): void {
    const ta = this.$('[data-il-input]') as HTMLTextAreaElement | null;
    if (ta) this._draft = ta.value;
  }

  /**
   * 执行手写的 IL。
   *
   * 与正式链路的唯一区别是「输入源」：这里没有 chat()，
   * 后面的 sanitize → parse → evaluate 完全一样。
   *
   * 注意这里**不做清洗** —— sanitize 是为模型输出准备的（它爱加围栏和
   * 前言）。人写的代码应该原样执行，看到自己写错才会有反馈。
   */
  private async runIL(): Promise<void> {
    if (this._running) return;
    this.syncDraft();
    const code = this._draft.trim();
    if (!code) return;

    this._running = true;
    this.update();

    let run: ILRun;

    try {
      const parsed = tryParse(code);
      if (!parsed.ok) {
        run = { at: Date.now(), code, parseError: formatParseError(parsed.error), results: [] };
      } else {
        const results = await evaluate(parsed.program);
        run = { at: Date.now(), code, parseError: '', results };
      }
    } catch (err) {
      // evaluate 本身设计成不抛异常，但万一有意料之外的错误，
      // 也不能让整个调试页崩掉 —— 那正是调试页最该可用的时候。
      run = {
        at: Date.now(),
        code,
        parseError: '',
        results: [
          {
            command: code,
            ok: false,
            desc: '',
            error: {
              kind: 'RuntimeError',
              message: err instanceof Error ? err.message : String(err),
            },
          },
        ],
      };
    }

    this._runs.unshift(run);
    if (this._runs.length > 20) this._runs.pop();
    this._running = false;
    this.update();

    // 手写的 IL 也打一份到控制台 —— 调试页执行的东西不该只有这一页看得到。
    // 排查「为什么同样的指令在这里行、在正式链路不行」时，
    // 控制台上两处输出挨着对比，一眼就能定位差异。
    // 走与正式链路同一套 trace，输出形状一致才好对比。
    trace.beginTurn(`[IL 直连] ${run.code}`);
    trace.note(`解析：${run.parseError ? `失败 —— ${run.parseError}` : '通过 ✓'}`);
    if (run.results) trace.evalResults(run.results);
    trace.endTurn();
  }

  connectedCallback(): void {
    super.connectedCallback();
    // 收集本会话内已经发生的 AI 调用
    this.subscribe('ai:response', ({ input, raw, code }) => {
      this._logs.unshift({ at: Date.now(), input, raw, code });
      if (this._logs.length > 30) this._logs.pop();
      if (this._tab === 'logs') this.update();
    });

    // 调试页是「开发模式」的专属界面 —— 既然人能打开它，
    // 说明他正在排查问题。这里主动打一份注册状态到控制台，
    // 免得还要去翻别的地方确认能力装没装上。
    // 用 settingsRepo 里的 debugMode 而不是 import.meta.env.DEV：
    // 生产构建里调试模式同样可以被用户打开，那时这份信息一样有用。
    if (settingsRepo.getApp().debugMode) {
      trace.note('能力清单', {
        对象数: registry.listObjects().length,
        方法数: registry.countMethods(),
        在内存中的句柄: describeHandles(),
      });
    }
  }
}

const BACK_ICON = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 18l-6-6 6-6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

/** 解析错误 → 一行可读文案（含出错位置） */
function formatParseError(err: { kind: string; message: string; pos?: { start: number } }): string {
  const at = err.pos ? `（第 ${err.pos.start + 1} 个字符处）` : '';
  return `${err.kind}${at}：${err.message}`;
}

/** 把查询返回值渲染成可折叠的预览。超长就截断，别把 DOM 撑爆 */
function safePreview(value: unknown): string {
  if (value === undefined) return '（没有数据）';
  try {
    const text = JSON.stringify(value, null, 2) ?? String(value);
    return text.length > 3000 ? `${text.slice(0, 3000)}\n…（已截断）` : text;
  } catch {
    return String(value);
  }
}

function formatParamsText(params?: { name: string; type: string; values?: string[]; required?: boolean }[]): string {
  if (!params || params.length === 0) return '';
  return params
    .map((p) => {
      const t = p.type === 'enum' && p.values ? p.values.map((v) => `'${v}'`).join('|') : p.type;
      return `${p.name}${p.required === false ? '?' : ''}: ${t}`;
    })
    .join(', ');
}

define('lx-debug-view', LxDebugView);
